import { createContext, useContext, useState } from 'react'
import { ScrollRestoration, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'
import { Header } from './Header'
import { Footer } from './Footer'
import { LoginModal } from './LoginModal'
import { RequireAuth } from './RequireAuth'
import { PageTitle } from './PageTitle'
import { recordNavigation } from './navHistory'
import { PlayerCardProvider } from '@/features/player/card/PlayerCardProvider'

// Lets a page widen the centered content shell beyond its default 1180px so the
// white panel + border/shadow grow WITH the content instead of the content
// spilling outside a fixed-width panel. Today the dashboard's wide multi-column
// grid uses it; pass a px number to widen, null to reset. Pages set it from a
// layout effect (see the dashboard) so the panel is sized before paint.
const ContentMaxWidthContext = createContext<(px: number | null) => void>(() => {})
export function useSetContentMaxWidth() {
  return useContext(ContentMaxWidthContext)
}

export function Layout() {
  const [contentMaxWidth, setContentMaxWidth] = useState<number | null>(null)
  // Record every navigation so the 404 page can report the prior in-app route.
  // Done during render (Layout renders before its route element) so the value is
  // set before the NotFound effect reads it; recordNavigation no-ops on repeats.
  recordNavigation(useLocation().pathname)
  return (
    <PlayerCardProvider>
    <ContentMaxWidthContext.Provider value={setContentMaxWidth}>
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />
      {/* Unified content shell — same at every breakpoint. No card
       *  chrome (rounded / shadow), no top gap below the Header.
       *  Content sits in a centered max-width column with bg-white +
       *  padding; on viewports wider than the column the slate page
       *  background shows on either side. Side edges combine a darker
       *  border with a soft horizontal shadow so the column reads as a
       *  defined panel rather than an unbounded white stripe. Pages
       *  with a secondary nav (teams, leagues) escape this padding
       *  with negative margins to extend the nav rail edge-to-edge
       *  under the Header. Width defaults to 1180px; a page can widen it
       *  via useSetContentMaxWidth (the dashboard's wide layouts). */}
      <main className="flex-1">
        <div
          className="mx-auto bg-white border-x border-slate-300 shadow-[0_0_8px_rgba(0,0,0,0.08)] px-1 sm:px-4 py-4 sm:py-6 min-h-[200px]"
          style={{ maxWidth: contentMaxWidth ?? 1180 }}
        >
          <RequireAuth />
        </div>
      </main>
      <Footer />
      <LoginModal />
      <PageTitle />
      <Toaster richColors closeButton position="top-center" />
      {/* Site-wide scroll behavior (UI-review #22). Keyed by pathname so a real
       *  route change (incl. secondary-nav tabs) scrolls to top, while an
       *  in-place search-param change (sort / filter / pill toggle) preserves
       *  the user's scroll position instead of yanking them to the top.
       *  Back/forward (POP) restores the saved position. Replaces the per-page
       *  `useScrollToTop` hook that used to live in the lineup cluster. */}
      <ScrollRestoration getKey={(location) => location.pathname} />
    </div>
    </ContentMaxWidthContext.Provider>
    </PlayerCardProvider>
  )
}
